import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Search, X } from "lucide-react"
import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import { cuppingsPath, cuppingPath, editCuppingPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface Sample {
  id: number
  name: string
  description: string
  origin: string
  producer: string
}

interface Tasting {
  id: number
  title: string
  details: string
  start_date: string
  end_date: string
  cupping_form: string
  samples: Sample[]
  roaster?: string
  humidity?: string
  density?: string
  water_activity?: string
  roast_profile?: string
  agtron_scale?: string
  brew_method?: string
  grind_size?: string
  brew_temperature?: string
  brew_ratio?: string
}

interface Props {
  cupping: Tasting
  existing_samples: Sample[]
}

export default function EditTasting({ cupping, existing_samples }: Props) {
  const { t } = useTranslation()
  const [sessionName, setSessionName] = useState(cupping?.title || "")
  const [scheduledDate, setScheduledDate] = useState(
    cupping?.start_date
      ? new Date(cupping?.start_date).toISOString().slice(0, 16)
      : "",
  )
  const [protocol, setProtocol] = useState(cupping?.cupping_form || "arabica")
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedSamples, setSelectedSamples] = useState<Sample[]>(
    cupping?.samples || [],
  )
  const [touched, setTouched] = useState({
    name: false,
    date: false,
    samples: false,
  })
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const hasRoastInfo = !!(
    cupping?.roaster ||
    cupping?.humidity ||
    cupping?.density ||
    cupping?.water_activity ||
    cupping?.roast_profile ||
    cupping?.agtron_scale
  )
  const [includeRoastInfo, setIncludeRoastInfo] = useState(hasRoastInfo)
  const [roastInfo, setRoastInfo] = useState({
    roaster: cupping?.roaster || "",
    humidity: cupping?.humidity || "",
    density: cupping?.density || "",
    waterActivity: cupping?.water_activity || "",
    roastProfile: cupping?.roast_profile || "",
    agtronScale: cupping?.agtron_scale || "",
  })

  const hasBrewInfo = !!(
    cupping?.brew_method ||
    cupping?.grind_size ||
    cupping?.brew_temperature ||
    cupping?.brew_ratio
  )
  const [includeBrewInfo, setIncludeBrewInfo] = useState(hasBrewInfo)
  const [brewInfo, setBrewInfo] = useState({
    method: cupping?.brew_method || "",
    grindSize: cupping?.grind_size || "",
    temperature: cupping?.brew_temperature || "",
    ratio: cupping?.brew_ratio || "",
  })

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("cuppings.title"),
      href: cuppingsPath(),
    },
    {
      title: cupping?.title || "",
      href: cuppingPath(cupping?.id || 0),
    },
    {
      title: t("cuppings.edit.page_title"),
      href: editCuppingPath(cupping?.id || 0),
    },
  ]

  const filteredSamples = useMemo(() => {
    const availableSamples = existing_samples.filter(
      (s) => !selectedSamples.some((sel) => sel.id === s.id),
    )
    if (!searchQuery) return availableSamples
    const query = searchQuery.toLowerCase()
    return availableSamples.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        s.origin?.toLowerCase().includes(query) ||
        s.producer?.toLowerCase().includes(query),
    )
  }, [existing_samples, searchQuery, selectedSamples])

  const handleSelectSample = (sample: Sample) => {
    setSelectedSamples([...selectedSamples, sample])
    setSearchQuery("")
  }

  const handleRemoveSample = (sampleId: number) => {
    setSelectedSamples(selectedSamples.filter((s) => s.id !== sampleId))
  }

  // Validation helpers
  const errors = {
    name: !sessionName.trim(),
    date: !scheduledDate,
    samples: selectedSamples.length === 0,
  }
  const hasErrors = errors.name || errors.date || errors.samples
  const showNameError = errors.name && (touched.name || submitAttempted)
  const showDateError = errors.date && (touched.date || submitAttempted)
  const showSamplesError =
    errors.samples && (touched.samples || submitAttempted)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitAttempted(true)

    if (hasErrors) {
      return
    }

    const data = {
      cupping: {
        title: sessionName,
        start_date: scheduledDate,
        cupping_form: protocol,
        ...(includeRoastInfo && {
          roaster: roastInfo.roaster,
          humidity: roastInfo.humidity || null,
          density: roastInfo.density || null,
          water_activity: roastInfo.waterActivity || null,
          roast_profile: roastInfo.roastProfile,
          agtron_scale: roastInfo.agtronScale || null,
        }),
        ...(!includeRoastInfo && {
          roaster: null,
          humidity: null,
          density: null,
          water_activity: null,
          roast_profile: null,
          agtron_scale: null,
        }),
        ...(includeBrewInfo && {
          brew_method: brewInfo.method,
          grind_size: brewInfo.grindSize || null,
          brew_temperature: brewInfo.temperature || null,
          brew_ratio: brewInfo.ratio,
        }),
        ...(!includeBrewInfo && {
          brew_method: null,
          grind_size: null,
          brew_temperature: null,
          brew_ratio: null,
        }),
        samples_attributes: selectedSamples.map((sample) => ({
          id: sample.id,
        })),
      },
    }

    router.put(cuppingPath(cupping.id), data)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Editar ${cupping.title}`} />

      <div className="flex h-full flex-1 flex-col gap-4 p-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Link href={cuppingsPath()}>
                <ArrowLeft className="text-muted-foreground hover:text-foreground h-5 w-5 cursor-pointer" />
              </Link>
              <div>
                <CardTitle>{t("cuppings.edit.title")}</CardTitle>
                <p className="text-muted-foreground text-sm">
                  {t("cuppings.edit.description")}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Session Info */}
              <div className="space-y-2">
                <Label
                  htmlFor="sessionName"
                  className={showNameError ? "text-destructive" : ""}
                >
                  {t("cuppings.edit.fields.session_name")} *
                </Label>
                <Input
                  id="sessionName"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  onBlur={() => setTouched({ ...touched, name: true })}
                  placeholder={t(
                    "cuppings.edit.fields.session_name_placeholder",
                  )}
                  className={showNameError ? "border-destructive" : ""}
                />
                {showNameError && (
                  <p className="text-destructive text-sm">
                    {t("cuppings.edit.fields.session_name_required")}
                  </p>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label
                    htmlFor="scheduledDate"
                    className={showDateError ? "text-destructive" : ""}
                  >
                    {t("cuppings.edit.fields.scheduled_date")} *
                  </Label>
                  <Input
                    id="scheduledDate"
                    type="datetime-local"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    onBlur={() => setTouched({ ...touched, date: true })}
                    className={showDateError ? "border-destructive" : ""}
                  />
                  {showDateError && (
                    <p className="text-destructive text-sm">
                      {t("cuppings.edit.fields.scheduled_date_required")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="protocol">
                    {t("cuppings.edit.fields.protocol")}
                  </Label>
                  <Select value={protocol} onValueChange={setProtocol}>
                    <SelectTrigger id="protocol">
                      <SelectValue
                        placeholder={t(
                          "cuppings.edit.fields.protocol_placeholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="arabica">
                        {t("cuppings.edit.protocols.arabica")}
                      </SelectItem>
                      <SelectItem value="robusta">
                        {t("cuppings.edit.protocols.robusta")}
                      </SelectItem>
                      <SelectItem value="cup_of_excellence">
                        {t("cuppings.edit.protocols.cup_of_excellence")}
                      </SelectItem>
                      <SelectItem value="sca_cva_affective">
                        {t("cuppings.edit.protocols.sca_cva_affective")}
                      </SelectItem>
                      <SelectItem value="sca_cva_descriptive">
                        {t("cuppings.edit.protocols.sca_cva_descriptive")}
                      </SelectItem>
                      <SelectItem value="sca_cva_combined">
                        {t("cuppings.edit.protocols.sca_cva_combined")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sample Selector */}
              <div className="space-y-3">
                <Label className={showSamplesError ? "text-destructive" : ""}>
                  {t("cuppings.edit.fields.select_samples")} *
                </Label>

                {selectedSamples.length > 0 && (
                  <div className="space-y-2">
                    {selectedSamples.map((sample, index) => (
                      <div
                        key={sample.id}
                        className="bg-primary/5 flex items-center justify-between rounded-lg border p-3"
                      >
                        <div className="flex items-center gap-3">
                          <span className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium">
                            {index + 1}
                          </span>
                          <div>
                            <p className="font-medium">{sample.name}</p>
                            <p className="text-muted-foreground text-sm">
                              {[sample.origin, sample.producer]
                                .filter(Boolean)
                                .join(" • ")}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveSample(sample.id)}
                          className="h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <div
                    className={`relative rounded-lg ${showSamplesError ? "ring-destructive ring-2" : ""}`}
                  >
                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setTouched({ ...touched, samples: true })}
                      placeholder={t(
                        "cuppings.edit.fields.search_samples_placeholder",
                      )}
                      className={`pl-9 ${showSamplesError ? "border-destructive" : ""}`}
                    />
                  </div>
                  {showSamplesError && (
                    <p className="text-destructive text-sm">
                      {t("cuppings.edit.fields.select_samples_required")}
                    </p>
                  )}
                  {existing_samples.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      {t("cuppings.edit.fields.no_samples_available")}{" "}
                      <Link
                        href="/samples/new"
                        className="text-primary hover:underline"
                      >
                        {t("cuppings.edit.fields.create_sample_first")}
                      </Link>
                      .
                    </p>
                  ) : filteredSamples.length === 0 && searchQuery ? (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      {t("cuppings.edit.fields.no_samples_found")} "
                      {searchQuery}"
                    </p>
                  ) : filteredSamples.length === 0 &&
                    selectedSamples.length > 0 ? (
                    <p className="text-muted-foreground py-2 text-center text-sm">
                      {t("cuppings.edit.fields.all_samples_selected")}
                    </p>
                  ) : filteredSamples.length > 0 ? (
                    <div className="max-h-48 divide-y overflow-y-auto rounded-lg border">
                      {filteredSamples.map((sample) => (
                        <button
                          key={sample.id}
                          type="button"
                          onClick={() => handleSelectSample(sample)}
                          className="hover:bg-accent w-full p-3 text-left transition-colors"
                        >
                          <p className="font-medium">{sample.name}</p>
                          <p className="text-muted-foreground text-sm">
                            {[sample.origin, sample.producer]
                              .filter(Boolean)
                              .join(" • ") ||
                              t("cuppings.edit.fields.no_additional_info")}
                          </p>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Roast Info Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeRoastInfo"
                  checked={includeRoastInfo}
                  onCheckedChange={(checked) =>
                    setIncludeRoastInfo(checked as boolean)
                  }
                />
                <Label
                  htmlFor="includeRoastInfo"
                  className="cursor-pointer text-sm font-medium"
                >
                  {t("cuppings.edit.fields.add_roast_info")}
                </Label>
              </div>

              {includeRoastInfo && (
                <div className="bg-background space-y-4 rounded-lg border p-4">
                  <h4 className="font-medium">
                    {t("cuppings.edit.fields.roast_info_title")}
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="roaster">
                        {t("cuppings.edit.fields.roaster")}
                      </Label>
                      <Input
                        id="roaster"
                        value={roastInfo.roaster}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            roaster: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.edit.fields.roaster_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="humidity">
                        {t("cuppings.edit.fields.humidity")}
                      </Label>
                      <Input
                        id="humidity"
                        type="number"
                        step="0.1"
                        value={roastInfo.humidity}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            humidity: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.edit.fields.humidity_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="density">
                        {t("cuppings.edit.fields.density")}
                      </Label>
                      <Input
                        id="density"
                        type="number"
                        value={roastInfo.density}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            density: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.edit.fields.density_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="waterActivity">
                        {t("cuppings.edit.fields.water_activity")}
                      </Label>
                      <Input
                        id="waterActivity"
                        type="number"
                        step="0.01"
                        value={roastInfo.waterActivity}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            waterActivity: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.edit.fields.water_activity_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="roastProfile">
                        {t("cuppings.edit.fields.roast_profile")}
                      </Label>
                      <Select
                        value={roastInfo.roastProfile}
                        onValueChange={(value) =>
                          setRoastInfo({ ...roastInfo, roastProfile: value })
                        }
                      >
                        <SelectTrigger id="roastProfile">
                          <SelectValue
                            placeholder={t(
                              "cuppings.edit.fields.roast_profile_placeholder",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="light">
                            {t("cuppings.edit.roast_profiles.light")}
                          </SelectItem>
                          <SelectItem value="medium">
                            {t("cuppings.edit.roast_profiles.medium")}
                          </SelectItem>
                          <SelectItem value="dark">
                            {t("cuppings.edit.roast_profiles.dark")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agtronScale">
                        {t("cuppings.edit.fields.agtron_scale")}
                      </Label>
                      <Input
                        id="agtronScale"
                        type="number"
                        value={roastInfo.agtronScale}
                        onChange={(e) =>
                          setRoastInfo({
                            ...roastInfo,
                            agtronScale: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.edit.fields.agtron_scale_placeholder",
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Brew Info Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeBrewInfo"
                  checked={includeBrewInfo}
                  onCheckedChange={(checked) =>
                    setIncludeBrewInfo(checked as boolean)
                  }
                />
                <Label
                  htmlFor="includeBrewInfo"
                  className="cursor-pointer text-sm font-medium"
                >
                  {t("cuppings.edit.fields.add_brew_info")}
                </Label>
              </div>

              {includeBrewInfo && (
                <div className="bg-background space-y-4 rounded-lg border p-4">
                  <h4 className="font-medium">
                    {t("cuppings.edit.fields.brew_info_title")}
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="method">
                        {t("cuppings.edit.fields.brew_method")}
                      </Label>
                      <Input
                        id="method"
                        value={brewInfo.method}
                        onChange={(e) =>
                          setBrewInfo({ ...brewInfo, method: e.target.value })
                        }
                        placeholder={t(
                          "cuppings.edit.fields.brew_method_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="grindSize">
                        {t("cuppings.edit.fields.grind_size")}
                      </Label>
                      <Input
                        id="grindSize"
                        type="number"
                        value={brewInfo.grindSize}
                        onChange={(e) =>
                          setBrewInfo({
                            ...brewInfo,
                            grindSize: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.edit.fields.grind_size_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="temperature">
                        {t("cuppings.edit.fields.temperature")}
                      </Label>
                      <Input
                        id="temperature"
                        type="number"
                        value={brewInfo.temperature}
                        onChange={(e) =>
                          setBrewInfo({
                            ...brewInfo,
                            temperature: e.target.value,
                          })
                        }
                        placeholder={t(
                          "cuppings.edit.fields.temperature_placeholder",
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ratio">
                        {t("cuppings.edit.fields.ratio")}
                      </Label>
                      <Input
                        id="ratio"
                        value={brewInfo.ratio}
                        onChange={(e) =>
                          setBrewInfo({ ...brewInfo, ratio: e.target.value })
                        }
                        placeholder={t(
                          "cuppings.edit.fields.ratio_placeholder",
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={selectedSamples.length === 0 || !sessionName.trim()}
                >
                  {t("cuppings.edit.actions.save")}
                </Button>
                <Link href={cuppingsPath()}>
                  <Button type="button" variant="outline" size="sm">
                    {t("cuppings.edit.actions.cancel")}
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
